/**
 * Etailize Error Handling Utilities
 * 
 * Provides comprehensive error handling for Etailize authentication flows,
 * including network failures, API errors, form validation errors, and OAuth errors.
 */

/**
 * Error types for Etailize operations
 */
export const ERROR_TYPES = {
    NETWORK: 'NETWORK',
    API: 'API',
    VALIDATION: 'VALIDATION',
    OAUTH: 'OAUTH',
    MARKETPLACE: 'MARKETPLACE',
    AUTHENTICATION: 'AUTHENTICATION',
    CONFIGURATION: 'CONFIGURATION'
};

/**
 * Error severity levels
 */
export const ERROR_SEVERITY = {
    LOW: 'LOW',
    MEDIUM: 'MEDIUM',
    HIGH: 'HIGH',
    CRITICAL: 'CRITICAL'
};

/**
 * Create a standardized error object
 * @param {string} type - Error type from ERROR_TYPES
 * @param {string} message - Error message
 * @param {string} severity - Error severity from ERROR_SEVERITY
 * @param {Object} details - Additional error details
 * @param {Object} context - Context information
 * @returns {Object} - Standardized error object
 */
export const createError = (type, message, severity = ERROR_SEVERITY.MEDIUM, details = {}, context = {}) => {
    return {
        id: generateErrorId(),
        type,
        message,
        severity,
        details,
        context,
        timestamp: new Date().toISOString(),
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null
    };
};

/**
 * Generate unique error ID
 * @returns {string} - Unique error identifier
 */
const generateErrorId = () => {
    return `etailize_error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};




/**
 * Handle OAuth flow errors
 * @param {string} errorType - OAuth error type
 * @param {string} errorDescription - OAuth error description
 * @param {Object} context - OAuth context
 * @param {Function} t - Translation function
 * @returns {Object} - Standardized error object
 */
export const handleOAuthError = (errorType, errorDescription, context = {}, t) => {
    let message = t('oauth_error_generic');
    let severity = ERROR_SEVERITY.MEDIUM;

    switch (errorType) {
        case 'access_denied':
            message = t('oauth_error_access_denied');
            severity = ERROR_SEVERITY.LOW;
            break;
        case 'invalid_request':
            message = t('oauth_error_invalid_request');
            severity = ERROR_SEVERITY.MEDIUM;
            break;
        case 'unauthorized_client':
            message = t('oauth_error_unauthorized_client');
            severity = ERROR_SEVERITY.HIGH;
            break;
        case 'unsupported_response_type':
            message = t('oauth_error_unsupported_response');
            severity = ERROR_SEVERITY.HIGH;
            break;
        case 'invalid_scope':
            message = t('oauth_error_invalid_scope');
            severity = ERROR_SEVERITY.MEDIUM;
            break;
        case 'server_error':
            message = t('oauth_error_server');
            severity = ERROR_SEVERITY.HIGH;
            break;
        case 'temporarily_unavailable':
            message = t('oauth_error_temporarily_unavailable');
            severity = ERROR_SEVERITY.MEDIUM;
            break;
        case 'popup_blocked':
            message = t('oauth_error_popup_blocked');
            severity = ERROR_SEVERITY.MEDIUM;
            break;
        case 'timeout':
            message = t('oauth_error_timeout');
            severity = ERROR_SEVERITY.LOW;
            break;
    }

    return createError(
        ERROR_TYPES.OAUTH,
        message,
        severity,
        {
            errorType,
            errorDescription,
            marketplace: context.marketplace?.name
        },
        context
    );
};

/**
 * Handle marketplace-specific errors
 * @param {string} marketplaceName - Name of the marketplace
 * @param {string} errorCode - Marketplace-specific error code
 * @param {string} errorMessage - Marketplace error message
 * @param {Function} t - Translation function
 * @returns {Object} - Standardized error object
 */
export const handleMarketplaceError = (marketplaceName, errorCode, errorMessage, t) => {
    let message = t('marketplace_error_generic', { marketplace: marketplaceName });
    let severity = ERROR_SEVERITY.MEDIUM;

    // Handle common marketplace error patterns
    if (errorCode?.includes('auth') || errorMessage?.toLowerCase().includes('authentication')) {
        message = t('marketplace_error_authentication', { marketplace: marketplaceName });
        severity = ERROR_SEVERITY.HIGH;
    } else if (errorCode?.includes('permission') || errorMessage?.toLowerCase().includes('permission')) {
        message = t('marketplace_error_permission', { marketplace: marketplaceName });
        severity = ERROR_SEVERITY.HIGH;
    } else if (errorCode?.includes('config') || errorMessage?.toLowerCase().includes('configuration')) {
        message = t('marketplace_error_configuration', { marketplace: marketplaceName });
        severity = ERROR_SEVERITY.MEDIUM;
    }

    return createError(
        ERROR_TYPES.MARKETPLACE,
        message,
        severity,
        {
            marketplace: marketplaceName,
            errorCode,
            originalMessage: errorMessage
        }
    );
};


/**
 * Log error for debugging and monitoring
 * @param {Object} error - Standardized error object
 * @param {Object} additionalContext - Additional context information
 */
export const logError = (error, additionalContext = {}) => {
    const logData = {
        ...error,
        additionalContext,
        url: window.location.href,
        userAgent: navigator.userAgent
    };

    // Log to console in development
    if (process.env.NODE_ENV === 'development') {
        console.group(`🚨 Etailize Error [${error.type}]`);
        console.error('Error:', error.message);
        console.log('Details:', error.details);
        console.log('Context:', error.context);
        console.log('Additional Context:', additionalContext);
        console.groupEnd();
    }

    // TODO: Send to monitoring service in production
    // sendToMonitoringService(logData);
};

/**
 * Get user-friendly error message with recovery suggestions
 * @param {Object} error - Standardized error object
 * @param {Function} t - Translation function
 * @returns {Object} - Error display object with message and recovery options
 */
export const getErrorDisplay = (error, t) => {
    
    return {
        id: error.id,
        title: getErrorTitle(error.type, t),
        message: error.message,
        severity: error.severity,
        recoveryOptions: null,
        showDetails: error.severity === ERROR_SEVERITY.HIGH || error.severity === ERROR_SEVERITY.CRITICAL,
        timestamp: error.timestamp
    };
};

const getErrorTitle = (errorType, t) => {
    switch (errorType) {
        case ERROR_TYPES.NETWORK:
            return t('error_title_network');
        case ERROR_TYPES.API:
            return t('error_title_api');
        case ERROR_TYPES.VALIDATION:
            return t('error_title_validation');
        case ERROR_TYPES.OAUTH:
            return t('error_title_oauth');
        case ERROR_TYPES.MARKETPLACE:
            return t('error_title_marketplace');
        case ERROR_TYPES.AUTHENTICATION:
            return t('error_title_authentication');
        case ERROR_TYPES.CONFIGURATION:
            return t('error_title_configuration');
        default:
            return t('error_title_generic');
    }
};

/**
 * Create error boundary handler for React components
 * @param {Function} onError - Error handler function
 * @param {Function} t - Translation function
 * @returns {Function} - Error boundary handler
 */
export const createErrorBoundaryHandler = (onError, t) => {
    return (error, errorInfo) => {
        const etailizeError = createError(
            ERROR_TYPES.AUTHENTICATION,
            t('component_error_generic'),
            ERROR_SEVERITY.HIGH,
            {
                componentStack: errorInfo.componentStack,
                errorBoundary: true,
                originalError: error.message
            }
        );

        logError(etailizeError, { errorInfo });
        
        if (onError) {
            onError(etailizeError);
        }
    };
};